/**
 * App — roteamento principal do GeoGuard UPM.
 *
 * Cada módulo M{N} é uma rota. Layout único (AppShell) com sidebar
 * + bottom nav mobile. Lazy loading não é necessário ainda dado o
 * tamanho da POC, mas pode ser adicionado quando os bundles crescerem.
 */

import { Navigate, Route, Routes } from 'react-router-dom';
import AppShell from '@/components/AppShell';
import { ProtectedRoute } from '@/components/Layout/ProtectedRoute';
import { useClearCacheOnSignOut } from '@/hooks/useClearCacheOnSignOut';
import { useGuardiaoDaSessao } from '@/hooks/useGuardiaoDaSessao';
import { useRegistroDeRetornoDeLogin } from '@/hooks/useRegistroDeRetornoDeLogin';
import { RoleGuard } from '@/components/Layout/RoleGuard';
import LoginPage from '@/pages/LoginPage';

import M1Dashboard from '@/features/M1-dashboard';
import M2Mapa from '@/features/M2-mapa';
import M3Tarp from '@/features/M3-tarp';
import PlanosAcao from '@/features/planos';
import M6Instrumentacao from '@/features/M6-instrumentacao';
import M6Grafico from '@/features/M6-grafico';
import M7FsQuinzenal from '@/features/M7-fs-quinzenal';
import M9Conformidade from '@/features/M9-conformidade';
import M10Log from '@/features/M10-log';
import M12Historico from '@/features/M12-historico';
import Outros from '@/features/outros';
import Paebm from '@/features/paebm';
import AuditoriaPage from '@/features/auditoria';
import HistoricoTrocasPage from '@/features/shift-handover/HistoricoTrocasPage';
import TotpEnrollmentPage from '@/features/shift-handover/TotpEnrollmentPage';
import LegalPlaceholder from '@/pages/LegalPlaceholder';
import {
  M4Sirenes, M5Cameras, M11Ia,
} from '@/features/placeholders';

export default function App() {
  // GUPM-241: um único listener de `SIGNED_OUT` para limpar o cache de queries.
  // Fica aqui, na raiz dentro do QueryClientProvider, e não no `useAuth` — que
  // tem 13 call sites e é montado em testes sem provider de query.
  useClearCacheOnSignOut();
  // GUPM-336: renova o JWT por tempo decorrido — o relógio do PC pode estar errado.
  useGuardiaoDaSessao();
  // GUPM-335: erro de login devolvido na URL vai para a trilha, uma vez.
  useRegistroDeRetornoDeLogin();

  return (
    <Routes>
      {/* Rota pública */}
      <Route path="/login" element={<LoginPage />} />

      {/* Rotas protegidas */}
      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard"      element={<M1Dashboard />} />
        <Route path="/mapa"           element={<M2Mapa />} />
        <Route path="/tarp"           element={<M3Tarp />} />
        <Route path="/planos"         element={<PlanosAcao />} />
        <Route path="/paebm"          element={<Paebm />} />
        <Route path="/sirenes"        element={<M4Sirenes />} />
        <Route path="/cameras"        element={<M5Cameras />} />
        <Route path="/instrumentacao"         element={<M6Grafico />} />
        <Route
          path="/instrumentacao/leitura"
          element={
            <RoleGuard allow={['admin', 'tecnico_responsavel']}>
              <M6Instrumentacao />
            </RoleGuard>
          }
        />
        <Route path="/fs-quinzenal"   element={<M7FsQuinzenal />} />
        <Route path="/conformidade"   element={<M9Conformidade />} />
        <Route path="/log"            element={<M10Log />} />
        <Route path="/ia"             element={<M11Ia />} />
        <Route path="/historico"      element={<M12Historico />} />
        <Route path="/outros"         element={<Outros />} />
        <Route
          path="/auditoria"
          element={
            <RoleGuard allow={['admin', 'tecnico_responsavel']}>
              <AuditoriaPage />
            </RoleGuard>
          }
        />
        <Route
          path="/historico-trocas"
          element={
            <RoleGuard allow={['admin', 'tecnico_responsavel']}>
              <HistoricoTrocasPage />
            </RoleGuard>
          }
        />
        <Route path="/perfil/totp"    element={<TotpEnrollmentPage />} />
        {/* GUPM-212 — rotas placeholder (menu de usuário aponta pra cá, hoje desabilitado). */}
        <Route path="/termos"         element={<LegalPlaceholder titulo="Termos de Uso" />} />
        <Route path="/privacidade"    element={<LegalPlaceholder titulo="Política de Privacidade" />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  );
}
